import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncOutlined } from '@ant-design/icons';
import { App, Button, Typography } from 'antd';
import { AxiosError } from 'axios';
import { delay } from 'lodash';

import { useWorkflowRun } from './data';

export type RunWorkflowButtonArgs = {
  workflowName: string;
  workflowCommand: string;
  toolTip: string;
  onReload?: () => void;
};

export const RunWorkflowButton = ({ workflowName, workflowCommand, toolTip, onReload }: RunWorkflowButtonArgs) => {
  const navigate = useNavigate();
  const [retryDelay, setRetryDelay] = useState(false);
  const { notification } = App.useApp();
  const triggerPull = useWorkflowRun();

  useEffect(() => {
    if (triggerPull.isSuccess) {
      notification.success({
        message: 'Workflow Started',
        description: (
          <span>
            The "{workflowName}" job has started. Note that it can take about a minute to complete. Watch the progress
            on the&nbsp;
            <Typography.Link onClick={() => navigate('/admin/automation/jobs')}>Automation &gt; Jobs</Typography.Link>
            &nbsp;Page.
          </span>
        ),
        duration: 15, // seconds, giving more time for the user to read
      });
    }

    if (triggerPull.isError) {
      const error = triggerPull.error as AxiosError;

      notification.error({
        message: 'Workflow failed to start, please try again.',
        description: error.message,
        duration: 0, // force user to close
      });
    }
  }, [navigate, notification, triggerPull.error, triggerPull.isError, triggerPull.isSuccess]);

  return (
    <Button
      icon={<SyncOutlined />}
      title={toolTip}
      onClick={() => {
        setRetryDelay(true);
        delay(() => {
          triggerPull.mutate(workflowCommand, { onSettled: () => onReload && onReload() });
          setRetryDelay(false);
        }, 800);
      }}
      loading={retryDelay || triggerPull.isPending}
    >
      Start
    </Button>
  );
};
